/**
 * The authored-frame agreement guard — a family-authored joint frame must
 * AGREE with the rig's own geometry before it is promoted to the top of the
 * reorientation ladder (field report 2026-09-23, Tripo rig presets).
 *
 * WHY. Authored frames are keyed by the family `detectRigFamily` reads from
 * bone NAMES, and outrank every measured tier (ADR-010 §11 — the hand wins).
 * That is only sound while the names still carry the roll convention the
 * frames were measured on. Tripo's rig presets broke it: one skeleton with a
 * single roll (local +Y down the bone, local +X anatomically forward),
 * renamed per preset — so `tiger-mixamo` inherited Mixamo's MIRRORED arm
 * frames (left forward = −X: a 180° flip on the left arm), `tiger-actorcore`
 * inherited native Tripo's +Z-forward limbs (now lateral: a 90° twist), and
 * `tiger-vrm1` (J_Bip names, no VRMC_vrm) inherited VRoid's −Y leg primary
 * (these legs point down +Y). The names were right; the roll was not. This is
 * detect-family.ts's own rule — a WRONG family is worse than no family —
 * applied per bone: a frame the rig contradicts is refused, loudly.
 *
 * THE CHECK, per (family, bone, side):
 *   primary   — the authored localPrimary, carried to world by the bone's
 *               world bind, against the MEASURED bone direction (joint → its
 *               ontology primary child). Skipped when the child is unmapped
 *               or co-located (nothing measured, nothing contradicted).
 *   secondary — only for frames that DECLARE their secondary's meaning
 *               (`secondaryMeans: 'anterior'`): the authored secondary, in
 *               world and orthogonalised against the bone axis, against the
 *               rig's anatomical forward at bind (its declared/promoted
 *               facing through the hub triad) orthogonalised the same way.
 *               Skipped when the bone lies too close to forward for the
 *               projection to name a direction (ill-conditioned).
 *   Frames with no declared meaning (the graft's per-specimen finger frames,
 *   whose secondary is DORSAL and correct by construction) get the primary
 *   check only.
 *
 * THE FALLBACK. A refused frame is REPLACED by the measured frame of the same
 * semantic — primary = the measured bone direction, secondary = anatomical
 * forward orthogonalised against it — so the twist alignment still pairs
 * forward-with-forward across the two sides (dropping to the ladder instead
 * would pair this side's canonical UP against the other side's authored
 * FORWARD on the arms: a built-in quarter-turn, measured 2026-07-31). When no
 * measured frame can be formed (no declared meaning, ill-conditioned, no
 * child), the refusal falls through to the ladder (null frame). Either way
 * the refusal is returned for the report — never silent.
 *
 * Depends on: math/quat, math/vec3, ir/priors (type), ir/rig-descriptor (type).
 */
import { quatInvertUnit, quatNormalize, quatRotateVec3 } from '../math/quat.js';
import { vec3Dot, vec3Length, vec3Normalize, vec3Scale, vec3Sub, type Vec3Data } from '../math/vec3.js';
import type { JointFrame } from '../ir/priors.js';
import type { RigBone } from '../ir/rig-descriptor.js';

/**
 * Largest disagreements (degrees) an authored frame may carry against the
 * rig's geometry and still be promoted. MEASURED on the corpus, not tuned on
 * the failing files — app/src/import/authored-frame-agreement-probe, run
 * 2026-09-23 over 541 field-accepted rigs of the five families the tables
 * key (tripo 275 incl. the finger-armed roster + World prebuilt + tripo-v2 +
 * HD intake + shoulder corpus; mixamo 245 = the whole Mixamo FBX library;
 * meshy 8; vrm 10 VRoid; sl 3):
 *
 *   accepted, worst       primary 51.3° (Wilbur's L_Forearm — a tiny arm
 *                         whose recentred wrist moved the child, not the
 *                         roll) · secondary 39.3° (Jim's forearm — the Tripo
 *                         forearm authoring the table header already calls
 *                         the noisiest)
 *   laundered, smallest   primary 180° (tiger-vrm1's legs) · secondary 81.8°
 *                         (tiger-actorcore's left forearm; the quarter-turn
 *                         class otherwise reads 88–107°, the mirrored class
 *                         172–180°)
 *
 * Each tolerance sits in its gap: 70° for the primary (between 51.3 and the
 * 90° axis-swap class), 60° for the secondary (between 39.3 and 81.8). A
 * disagreement INSIDE the accepted band is authoring noise the field has
 * already judged; the guard is for the roll classes, not for re-litigating it.
 */
export const AUTHORED_PRIMARY_TOLERANCE_DEG = 70;
export const AUTHORED_SECONDARY_TOLERANCE_DEG = 60;

/**
 * The secondary check needs the bone to stand well off anatomical forward:
 * forward's component ⊥ the bone must be at least sin(30°) of it, or the
 * projected direction is dominated by how the bone happens to lean. Every
 * authored bone today (limbs, clavicles, spine) stands near-perpendicular to
 * forward; a bone within 60° of forward is skipped, not judged.
 */
const SECONDARY_CONDITIONING = Math.sin((30 * Math.PI) / 180);

export type AuthoredFrameRefusalReason = 'primary-disagrees' | 'secondary-disagrees';

export interface AuthoredFrameRefusal {
  readonly family: string;
  readonly ontologyId: string;
  /** The rig bone the frame was offered for. */
  readonly bone: string;
  readonly side: 'source' | 'target';
  readonly reason: AuthoredFrameRefusalReason;
  /** The disagreement that refused it, degrees. */
  readonly angleDeg: number;
  readonly toleranceDeg: number;
  /** What the bone used instead. */
  readonly fallback: 'measured-anterior-frame' | 'ladder';
}

export interface AuthoredFrameAgreement {
  /** Authored primary vs measured bone direction, degrees; null = not measurable. */
  readonly primaryDeg: number | null;
  /** Authored secondary vs anatomical forward (both ⊥ the bone), degrees; null = not judged. */
  readonly secondaryDeg: number | null;
}

export interface AuthoredFrameVerdict extends AuthoredFrameAgreement {
  /** The frame to hand the ladder: the authored one, its measured replacement, or null. */
  readonly frame: JointFrame | null;
  readonly refusal: AuthoredFrameRefusal | null;
}

const DEG = 180 / Math.PI;

function angleDeg(a: Vec3Data, b: Vec3Data): number {
  return Math.acos(Math.max(-1, Math.min(1, vec3Dot(a, b)))) * DEG;
}

/** `v` with its component along unit `axis` removed. */
function perpendicular(v: Vec3Data, axis: Vec3Data): Vec3Data {
  return vec3Sub(v, vec3Scale(axis, vec3Dot(v, axis)));
}

export interface AuthoredFrameGuardInput {
  readonly frame: JointFrame;
  readonly bone: RigBone;
  /** World bind position of the bone's ontology primary child, when mapped. */
  readonly childWorldPosition: Vec3Data | null;
  /** The rig's anatomical forward at bind, in rig world (hub +Z through the declared triad); null = no convention. */
  readonly anteriorWorld: Vec3Data | null;
  /** Co-location floor for the child offset, rig units. */
  readonly tolerance: number;
  readonly side: 'source' | 'target';
}

/**
 * Judge one authored frame against the rig it is about to be promoted over.
 * Pure; returns the frame to use plus the refusal (if any) for the report.
 */
export function vetAuthoredFrame(input: AuthoredFrameGuardInput): AuthoredFrameVerdict {
  const { frame, bone } = input;
  const rot = quatNormalize(bone.worldBind.rotation);
  const toLocal = quatInvertUnit(rot);
  const authoredPrimary = vec3Normalize(quatRotateVec3(rot, frame.localPrimary));

  let measuredPrimary: Vec3Data | null = null;
  if (input.childWorldPosition !== null) {
    const offset = vec3Sub(input.childWorldPosition, bone.worldBind.position);
    if (vec3Length(offset) > input.tolerance) measuredPrimary = vec3Normalize(offset);
  }
  const primaryDeg = measuredPrimary === null ? null : angleDeg(authoredPrimary, measuredPrimary);

  // The bone axis both secondaries are orthogonalised against: the MEASURED
  // direction when there is one — the geometry is the judge, not the claim.
  const axis = measuredPrimary ?? authoredPrimary;
  let forwardPerp: Vec3Data | null = null;
  if (input.anteriorWorld !== null) {
    const f = perpendicular(vec3Normalize(input.anteriorWorld), axis);
    if (vec3Length(f) >= SECONDARY_CONDITIONING) forwardPerp = vec3Normalize(f);
  }
  let secondaryDeg: number | null = null;
  if (frame.secondaryMeans === 'anterior' && forwardPerp !== null) {
    const s = perpendicular(quatRotateVec3(rot, frame.localSecondary), axis);
    if (vec3Length(s) > 1e-6) secondaryDeg = angleDeg(vec3Normalize(s), forwardPerp);
  }

  const reason: AuthoredFrameRefusalReason | null =
    primaryDeg !== null && primaryDeg > AUTHORED_PRIMARY_TOLERANCE_DEG
      ? 'primary-disagrees'
      : secondaryDeg !== null && secondaryDeg > AUTHORED_SECONDARY_TOLERANCE_DEG
        ? 'secondary-disagrees'
        : null;
  if (reason === null) return { frame, refusal: null, primaryDeg, secondaryDeg };

  // Refused. Replace with the measured frame of the SAME semantic when one
  // can be formed; otherwise hand the bone back to the ladder.
  const replacement: JointFrame | null =
    frame.secondaryMeans === 'anterior' && forwardPerp !== null
      ? {
          ...frame,
          localPrimary: vec3Normalize(quatRotateVec3(toLocal, axis)),
          localSecondary: vec3Normalize(quatRotateVec3(toLocal, forwardPerp)),
          provenance: ['measured:anterior-at-bind', `refused:${frame.provenance.join('+')}`],
        }
      : null;
  return {
    frame: replacement,
    primaryDeg,
    secondaryDeg,
    refusal: {
      family: frame.family,
      ontologyId: frame.ontologyId,
      bone: bone.name,
      side: input.side,
      reason,
      angleDeg: reason === 'primary-disagrees' ? (primaryDeg as number) : (secondaryDeg as number),
      toleranceDeg: reason === 'primary-disagrees' ? AUTHORED_PRIMARY_TOLERANCE_DEG : AUTHORED_SECONDARY_TOLERANCE_DEG,
      fallback: replacement === null ? 'ladder' : 'measured-anterior-frame',
    },
  };
}

export interface MeasuredAnteriorInput {
  readonly family: string;
  readonly ontologyId: string;
  readonly bone: RigBone;
  readonly childWorldPosition: Vec3Data | null;
  readonly anteriorWorld: Vec3Data | null;
  readonly tolerance: number;
}

/**
 * The bone's MEASURED anterior frame — primary = the measured bone direction
 * (joint → primary child), secondary = the rig's anatomical forward at bind
 * orthogonalised against it — or null when either is unmeasurable (child
 * missing or co-located, no convention, or the bone within 60° of forward).
 *
 * Two consumers. (1) A refused authored frame's replacement (above). (2) The
 * PAIRING rule: when the OTHER side of a pair carries an anterior frame and
 * this side has none, this side must name anterior too — the twist alignment
 * pairs secondaries across sides, and the ladder's non-authored fallback is
 * the ontology's canonical secondary, which on the arm chain is UP. Mixing
 * them is the quarter-turn the Mixamo arm frames' header measured on their
 * first authoring (2026-07-31), and it still fired wherever a Mixamo source
 * met a target family with no arm frames: measured 2026-09-23 with the
 * Mixamo walk, upper arms twisted 90° on every VRoid (Bobby, Carol, Eliza),
 * 101–103° on the Meshy biped, 93° on tiger-vrm1 — and on every family-less
 * target (the UE5 and Unity presets once their names map).
 */
export function measuredAnteriorFrame(input: MeasuredAnteriorInput): JointFrame | null {
  if (input.childWorldPosition === null || input.anteriorWorld === null) return null;
  const offset = vec3Sub(input.childWorldPosition, input.bone.worldBind.position);
  if (vec3Length(offset) <= input.tolerance) return null;
  const axis = vec3Normalize(offset);
  const f = perpendicular(vec3Normalize(input.anteriorWorld), axis);
  if (vec3Length(f) < SECONDARY_CONDITIONING) return null;
  const toLocal = quatInvertUnit(quatNormalize(input.bone.worldBind.rotation));
  return {
    family: input.family,
    ontologyId: input.ontologyId,
    localPrimary: vec3Normalize(quatRotateVec3(toLocal, axis)),
    localSecondary: vec3Normalize(quatRotateVec3(toLocal, vec3Normalize(f))),
    secondaryMeans: 'anterior',
    provenance: ['measured:anterior-at-bind'],
  };
}
